import i18n from 'i18next';

import { I18N_CORE_NAMESPACES, type I18nLazyNamespace } from '@/config/i18n-namespace-files';

const inflight = new Map<string, Promise<void>>();

export function preloadI18nNamespaces(namespaces: string | string[], language = i18n.language): Promise<void> {
  const lng = language?.split('-')[0] || 'en';
  const list = (Array.isArray(namespaces) ? namespaces : [namespaces]).filter(
    (ns) => !I18N_CORE_NAMESPACES.includes(ns as (typeof I18N_CORE_NAMESPACES)[number]),
  );

  return Promise.all(
    list.map((ns) => {
      if (i18n.hasResourceBundle(lng, ns)) return Promise.resolve();

      const key = `${lng}:${ns}`;
      const existing = inflight.get(key);
      if (existing) return existing;

      const promise = i18n.loadNamespaces(ns).finally(() => {
        inflight.delete(key);
      });
      inflight.set(key, promise);
      return promise;
    }),
  ).then(() => undefined);
}

/** Best-effort preload from org-scoped path (avoids translation key flash on navigation). */
export function preloadI18nForPath(pathname: string): void {
  const match = pathname.match(/\/org\/[^/]+\/([^/?]+)/);
  const segment = match?.[1];
  if (!segment) return;

  const bySegment: Record<string, I18nLazyNamespace[]> = {
    orders: ['orders'],
    subscriptions: ['subscriptions'],
    payments: ['payments'],
    disputes: ['disputes'],
    customers: ['customers'],
    stores: ['stores', 'products', 'rx'],
    processors: ['processors', 'paymentFlows'],
    'payment-flows': ['paymentFlows'],
    domains: ['domains'],
    'external-requests': ['externalRequests'],
    analytics: ['analytics'],
    reports: ['reports'],
    billing: ['settings'],
    profile: ['profile'],
    referral: ['referral'],
    setup: ['setup'],
    settings: ['settings'],
  };

  const namespaces = bySegment[segment];
  if (namespaces?.length) {
    void preloadI18nNamespaces(namespaces);
  }
}
